/**
 * Раздел «Склад»: запасы и оборачиваемость.
 *
 * Периода и города в панели нет, и это сказано словами: остатки — снимок
 * на последнюю дату, а складские отчёты не делятся по филиалам (склад —
 * своё измерение). Ограниченному по филиалу человеку сервер отказывает
 * в отчёте целиком, и блок объясняет это текстом сервера.
 */

import { Icon } from '../components/Icon'
import { useState, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import type { CurrentUser } from '../types'
import { Drawer, type DrawerOrigin } from '../finance/Drawer'
import { Freshness } from '../finance/parts'
import { ClosedSection } from '../finance/frame'
import { reportRefs } from '../finance/FinanceBlock'
import { useFreshness } from '../finance/freshness'
import '../finance/finance.css'
import './insights.css'
import { STOCK_SECTIONS, stockPath, visibleStockSections, type StockSection } from './sections'
import { useInsightsNavigator } from './InsightsNav'
import { SectionTabs } from './parts'
import { Stock, WarehouseDrill, type WarehouseTarget } from './stock/Stock'
import { Turnover } from './stock/Turnover'
import { formatDate } from '../format'
import { useStockSnapshotDate } from './stock/queries'
import { StockAccessContext } from './stock/access'

export function useStockSection(): StockSection {
  const { section } = useParams()
  return (STOCK_SECTIONS.find((item) => item.code === section)?.code ?? 'stock') as StockSection
}

export function StockBlock({ user }: { user: CurrentUser }) {
  const section = useStockSection()
  const { section: requested } = useParams()
  const go = useInsightsNavigator()
  const sections = visibleStockSections(user)
  const asOf = useFreshness()
  const snapshot = useStockSnapshotDate(user.reports.some((report) => report.code === 'stock_current'))
  const [drill, setDrill] = useState<{ target: WarehouseTarget; origin: DrawerOrigin } | null>(null)
  const [open, setOpen] = useState(false)

  if (!sections.some((item) => item.code === section)) {
    if (requested && STOCK_SECTIONS.some((item) => item.code === requested)) {
      const closed = STOCK_SECTIONS.find((item) => item.code === requested)!
      const first = sections[0]
      return (
        <ClosedSection
          title={closed.title}
          sectionCode={`stock.${closed.code}`}
          reports={reportRefs(closed.code === 'turnover' ? 'stock_turnover' : 'stock_current')}
          user={user}
          fallback={first ? { path: stockPath(first.code), label: `Открыть «${first.title}»` } : { path: '/', label: 'На главную' }}
        />
      )
    }
    return sections.length > 0 ? <Navigate to={stockPath(sections[0]!.code)} replace /> : <Navigate to="/" replace />
  }

  let body: ReactNode
  if (section === 'turnover') body = <Turnover />
  else
    body = (
      <Stock
        onOpenWarehouse={(target, origin) => {
          setDrill({ target, origin })
          setOpen(true)
        }}
      />
    )

  const has = (code: string) => user.reports.some((report) => report.code === code)
  const access = { current: has('stock_current'), turnover: has('stock_turnover'), margin: has('margin_by_department') }

  return (
    <StockAccessContext.Provider value={access}>
      <div className="fin">
        <div className="fin-toolbar">
          <span className="snapshot-chip">
            <Icon name="stock" className="snapshot-chip__icon" />
            {snapshot ? `Остатки на ${formatDate(snapshot)}` : 'Остатки на последнюю дату снимка'}
          </span>
          <span className="small muted">Вся компания: складские отчёты не делятся по городам</span>
          <span className="spacer" />
          <Freshness asOf={asOf.asOf} failed={asOf.failed} />
        </div>
        <SectionTabs
          label="Разделы склада"
          items={sections.map((item) => ({ path: stockPath(item.code), short: item.short, active: item.code === section }))}
          onGo={go}
        />
        <div className="fin-section" key={section}>
          {body}
        </div>
      </div>
      <Drawer
        open={open}
        origin={drill?.origin ?? null}
        onClose={() => setOpen(false)}
        title={drill?.target.label ?? ''}
        subtitle={drill ? `Склад · остатки на ${snapshot ? formatDate(snapshot) : 'последнюю дату'}` : undefined}
      >
        {drill && <WarehouseDrill target={drill.target} />}
      </Drawer>
    </StockAccessContext.Provider>
  )
}
